import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Select, { StylesConfig } from "react-select";
import { Country, State } from "country-state-city";
import { useTheme } from "../../contexts/ThemeContext";
import {
  useEditOrganisationMutation,
  useGetOrganisationByIdQuery,
} from "../../services";
import { toast } from "../../core/toast";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { adminRoutes } from "../../routers/AdminRouters";
import PhoneInputField from "../../components/PhoneInputField";

type Option = { value: string; label: string; countryCode?: string };

interface ExtendedFormState {
  name: string;
  displayName: string | null;
  email: string;
  phone: string;
  address: string;
  taxId: string;
  city: string;
  state: string;
  stateCode: string;
  country: string;
  countryCode: string;
  postal: string;
}

interface FormErrors {
  name?: string;
  displayName?: string;
  phone?: string;
  address?: string;
  country?: string;
  state?: string;
  city?: string;
  postal?: string;
}

const OrganisationEdit: React.FC = () => {
  const { theme } = useTheme();
  const { orgid } = useParams<{ orgid: string }>();
  const navigate = useNavigate();

  const {
    data: organisation,
    isLoading,
    isError,
  } = useGetOrganisationByIdQuery(orgid!, {
    skip: !orgid,
  });

  const [editOrganisation, { isLoading: isUpdating }] =
    useEditOrganisationMutation();

  const [initialForm, setInitialForm] = useState<ExtendedFormState | null>(null);
  const [form, setForm] = useState<ExtendedFormState>({
    name: "",
    displayName: "",
    email: "",
    phone: "",
    address: "",
    taxId: "",
    // currency: "",
    city: "",
    state: "",
    stateCode: "",
    country: "",
    countryCode: "",
    postal: "",
  });

  // inline errors
  const [errors, setErrors] = useState<FormErrors>({});

  // Options
  const countryOptions: Option[] = useMemo(
    () =>
      Country.getAllCountries().map((c) => ({
        value: c.isoCode,
        label: `${c.name} - ${c.isoCode}`,
      })),
    []
  );

  const allStates: Option[] = useMemo(
    () =>
      State.getAllStates().map((s) => ({
        value: `${s.countryCode}:${s.isoCode}`,
        label: s.name,
        countryCode: s.countryCode,
      })),
    []
  );

  const statesForSelectedCountry = useMemo(() => {
    if (!form.countryCode) return allStates;
    return allStates.filter((s) => s.countryCode === form.countryCode);
  }, [form.countryCode, allStates]);

  useEffect(() => {
    if (organisation) {
      // Logic to find Country Code and State Code from Names
      const orgCountryName = organisation.country || "";
      const orgStateName = organisation.state || "";

      const foundCountry = Country.getAllCountries().find(
        (c) =>
          c.name.toLowerCase() === orgCountryName.toLowerCase() ||
          c.isoCode.toLowerCase() === orgCountryName.toLowerCase()
      );
      const countryCode = foundCountry?.isoCode || "";
      const countryName = foundCountry
        ? `${foundCountry.name} - ${foundCountry.isoCode}`
        : orgCountryName;

      let stateCode = "";
      if (countryCode && orgStateName) {
        const foundState = State.getStatesOfCountry(countryCode).find(
          (s) => s.name.toLowerCase() === orgStateName.toLowerCase()
        );
        stateCode = foundState?.isoCode || "";
      }

      const anyOrg = organisation as any;
      const initialValues: ExtendedFormState = {
        name: organisation.name || "",
        displayName: organisation.displayName || null,
        email: organisation.email || "",
        phone: String(organisation.phone || ""),
        address: organisation.address || anyOrg.billing || "",
        taxId: organisation.taxId || "",
        city: organisation.city || "",
        state: organisation.state || "",
        stateCode: stateCode,
        country: countryName,
        countryCode: countryCode,
        postal: organisation.postal || anyOrg.zipCode || "",
      };
      setForm(initialValues);
      setInitialForm(initialValues);
    }
  }, [organisation]);

  // THEME CLASSES (match AdminCreate)
  const pageBg = theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50";
  const titleText = theme === "dark" ? "text-gray-100" : "text-gray-900";
  const crumbText = theme === "dark" ? "text-gray-400" : "text-gray-500";
  const crumbSep = theme === "dark" ? "text-gray-600" : "text-gray-400";
  const cardClass =
    theme === "dark"
      ? "bg-[#0F0F0F] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";
  const labelClass = theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700";
  const inputBase =
    "w-full rounded-md px-3 py-2 outline-none transition-colors";
  const inputClass =
    theme === "dark"
      ? `${inputBase} bg-[#111111] border border-[#2C2C2C] placeholder-gray-500 focus:border-[#B6CB24] text-gray-100`
      : `${inputBase} bg-white border border-gray-300 placeholder-gray-500 focus:border-[#B6CB24] text-gray-900`;

  // react-select theme-aware styles
  const selectStyles: StylesConfig<Option, false> = useMemo(() => {
    const isDark = theme === "dark";
    const bg = isDark ? "#111111" : "#ffffff";
    const menuBg = isDark ? "#111111" : "#ffffff";
    const border = isDark ? "#2C2C2C" : "#d1d5db";
    const hoverBorder = "#B6CB24";
    const focusedBg = isDark ? "#1E1E1E" : "#f3f4f6";
    const text = isDark ? "#e5e7eb" : "#111827";
    const subText = isDark ? "#9ca3af" : "#6b7280";

    return {
      control: (base, state) => ({
        ...base,
        backgroundColor: bg,
        borderColor: state.isFocused ? hoverBorder : border,
        boxShadow: "none",
        minHeight: 40,
        ":hover": { borderColor: state.isFocused ? hoverBorder : border },
      }),
      menu: (base) => ({ ...base, backgroundColor: menuBg, zIndex: 50 }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? focusedBg : menuBg,
        color: text,
        cursor: "pointer",
      }),
      singleValue: (base) => ({ ...base, color: text }),
      placeholder: (base) => ({ ...base, color: subText }),
      input: (base) => ({ ...base, color: text }),
      valueContainer: (base) => ({ ...base, paddingInline: 8 }),
      dropdownIndicator: (base) => ({ ...base, color: subText }),
      clearIndicator: (base) => ({ ...base, color: subText }),
      indicatorSeparator: (base) => ({ ...base, backgroundColor: border }),
    };
  }, [theme]);

  const handleTextChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const cleaned = value.replace(/[^\d+]/g, "");
      setForm((f) => ({ ...f, phone: cleaned }));
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // const handleEmailBlur = () => {
  //   const email = form.email.trim();
  //   if (!email) {
  //     setErrors((prev) => ({ ...prev, email: undefined }));
  //     return;
  //   }
  //   setErrors((prev) => ({
  //     ...prev,
  //     email: !emailRegex.test(email) ? "Email is invalid" : undefined,
  //   }));
  // };

  const onCountryChange = (opt: Option | null) => {
    if (!opt) {
      setForm((f) => ({
        ...f,
        country: "",
        countryCode: "",
        state: "",
        stateCode: "",
      }));
      return;
    }
    setForm((f) => ({
      ...f,
      country: opt.label,
      countryCode: opt.value,
      state: f.countryCode === opt.value ? f.state : "",
      stateCode: f.countryCode === opt.value ? f.stateCode : "",
    }));
    if (errors.country) setErrors((prev) => ({ ...prev, country: undefined }));
  };

  const onStateChange = (opt: Option | null) => {
    if (!opt) {
      setForm((f) => ({ ...f, state: "", stateCode: "" }));
      return;
    }
    const [countryCode, stateCode] = String(opt.value).split(":");
    const country = Country.getAllCountries().find(
      (c) => c.isoCode === countryCode
    );
    setForm((f) => ({
      ...f,
      state: opt.label,
      stateCode,
      country: country?.name || f.country,
      countryCode: country?.isoCode || f.countryCode,
    }));
    if (errors.state) setErrors((prev) => ({ ...prev, state: undefined }));
  };

  const onCurrencyChange = (opt: Option | null) => {
    setForm((f) => ({ ...f, currency: opt?.value || "" }));
  };

  const validateForm = () => {
    setErrors({});
    return true;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgid) return;
    if (!validateForm()) return;

    try {
      const data: any = {};
      let isChanged = false;

      const fieldsToCompare: (keyof ExtendedFormState)[] = [
        "name", "displayName", "email", "phone", "address",
        "taxId", "city", "state", "countryCode", "postal"
      ];

      fieldsToCompare.forEach(field => {
        let currentValue: string | null = String(form[field] || "").trim();
        let initialValue: string | null = String(initialForm?.[field] || "").trim();

        if (field === "displayName") {
          currentValue = currentValue || null;
          initialValue = initialValue || null;
        }

        if (currentValue !== initialValue) {
          isChanged = true;
          // Apply specific mapping for 'country' if the field is countryCode
          if (field === "countryCode") {
            data.country = currentValue;
          } else {
            data[field] = currentValue;
          }
        }
      });

      if (!isChanged) {
        // toast.success("No changes detected. Redirecting...");
        navigate(adminRoutes.organisationList);
        return;
      }

      await editOrganisation({
        orgid,
        data,
      }).unwrap();

      navigate(adminRoutes.organisationList);
    } catch (error) {
      console.error("Failed to update organisation:", error);
    }
  };

  const selectedCountry = form.countryCode
    ? { value: form.countryCode, label: form.country }
    : null;

  const selectedState =
    form.stateCode && form.countryCode
      ? { value: `${form.countryCode}:${form.stateCode}`, label: form.state }
      : form.state
        ? { value: form.state, label: form.state }
        : null;

  // const selectedCurrency =
  //   form.currency && currencyOptions.find((c) => c.value === form.currency)
  //     ? currencyOptions.find((c) => c.value === form.currency) || null
  //     : null;

  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  if (isLoading) {
    return (
      <div className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}>
        <div className="flex items-center justify-center h-64">
          <p className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (isError || !organisation) {
    return (
      <div className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}>
        <div className="flex items-center justify-center h-64">
          <p className={theme === "dark" ? "text-red-400" : "text-red-600"}>
            Failed to load organisation details.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className={`text-xl font-semibold ${titleText}`}>
            Organization Overview
          </h1>
          <div className="text-sm mt-1">
            <Link to={adminRoutes.organisationList} className={crumbText}>
              Dashboard
            </Link>
            <span className={crumbSep}> &nbsp;&gt;&nbsp; </span>
            <span className="text-[#B6CB24] font-medium">
              Edit Organisation
            </span>
          </div>
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        className={`${cardClass} rounded-lg p-2 md:p-4`}
      >
        <h2 className="text-lg font-semibold mb-2 border-b pb-1 border-gray-700">
          Edit Organisation
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>
              Organisation Legal Name <span className="text-red-500">*</span>
            </label>
            <input
              name="name"
              value={form.name}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={`${inputClass} ${errors.name ? "border-red-500" : ""}`}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>
          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>
              Display Name (If different legal company name)
            </label>
            <input
              name="displayName"
              value={form.displayName || ""}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={inputClass}
            />
          </div>
          <PhoneInputField
            label="Phone Number"
            value={form.phone}
            onChange={(value) => {
              setForm((f) => ({ ...f, phone: value }));
              if (errors.phone)
                setErrors((prev) => ({ ...prev, phone: undefined }));
            }}
            error={errors.phone}
            theme={theme}
          />
        </div>

        {/* <div className="flex flex-col md:flex-row gap-6 mb-6">
          <div className="flex-grow basis-0 md:max-w-xs space-y-4">
            <div>
              <label className={`block text-sm mb-2 ${labelClass}`}>
                Tax ID (Optional)
              </label>
              <input
                name="taxId"
                value={form.taxId}
                onChange={handleTextChange}
                placeholder="Enter here"
                className={inputClass}
              />
            </div>
            <div>
              <label className={`block text-sm mb-2 ${labelClass}`}>
                Choose Currency
              </label>
              <Select<Option, false>
                instanceId="currency-select"
                styles={selectStyles}
                isClearable
                placeholder="Choose from list"
                options={currencyOptions}
                value={selectedCurrency}
                onChange={onCurrencyChange}
                classNamePrefix="rs"
              />
            </div>
          </div>
          <div className="flex-grow-[2] basis-0">
            <label className={`block text-sm mb-2 ${labelClass}`}>
              Billing Address
            </label>
            <textarea
              name="address"
              value={form.address}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={`${inputClass} min-h-[150px] resize-y`}
            />
          </div>
        </div> */}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-8">
          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>
              Country
            </label>
            <Select<Option, false>
              instanceId="country-select"
              styles={selectStyles}
              isClearable
              isSearchable
              placeholder="Choose from list"
              options={countryOptions}
              value={selectedCountry}
              onChange={onCountryChange}
              classNamePrefix="rs"
            />
            {errors.country && (
              <p className="text-red-500 text-xs mt-1">{errors.country}</p>
            )}
          </div>

          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>State</label>
            <Select<Option, false>
              instanceId="state-select"
              styles={selectStyles}
              isClearable
              isSearchable
              placeholder="Choose from list"
              options={statesForSelectedCountry}
              value={selectedState}
              onChange={onStateChange}
              classNamePrefix="rs"
            />
            {errors.state && (
              <p className="text-red-500 text-xs mt-1">{errors.state}</p>
            )}
          </div>

          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>City</label>
            <input
              name="city"
              value={form.city}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={inputClass}
            />
          </div>

          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>
              Postal / Zip Code
            </label>
            <input
              name="postal"
              value={form.postal}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={inputClass}
            />
          </div>
          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>
              Tax ID (Optional)
            </label>
            <input
              name="taxId"
              value={form.taxId}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={inputClass}
            />
          </div>
        </div>
        <div className="flex-grow-[2] ">
          <label className={`block text-sm mb-0 ${labelClass}`}>
            Billing Address
          </label>
          <textarea
            name="address"
            value={form.address}
            onChange={handleTextChange}
            placeholder="Enter here"
            className={`${inputClass} min-h-[150px] resize-y`}
          />
        </div>
        <div
          className={
            theme === "dark"
              ? "border-t border-[#2C2C2C] my-4"
              : "border-t border-gray-200 my-4"
          }
        />

        <div className="flex items-center gap-4 mt-2">
          <button
            type="submit"
            disabled={isUpdating}
            className={`px-8 py-1.5 rounded-md font-medium ${theme === "dark"
              ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`} style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
              }}
          >
            {isUpdating ? "Updating..." : "Update"}
          </button>
          <Link
            to={`${adminRoutes.organisationList}`}
            className={`px-8 py-1.5 rounded-[10px] border border-gray-600 font-medium ${theme === "dark"
              ? "bg-transparent text-gray-300 hover:bg-[#2C2C2C]"
              : "bg-transparent text-gray-700 hover:bg-gray-100"
              }`}
          >
            Not Now
          </Link>
        </div>
      </form>
    </div>
  );
};

export default OrganisationEdit;
